/**
 * Plans (docs/WORKSPACES.md, prices from docs/VALUE.md §3). The number shown to the owner; nothing is charged here.
 *
 * G1: each active person counts once per business, at their main location. A person counts only once they've joined
 * (signed in, so they have a userId) or checked in (a shop-tablet person with no account): an invite alone costs nothing
 * (docs/ux/LAUNCH-PROPOSALS.md Wave 1 #8). A one-event volunteer stops counting the day after their `endsOn`; the
 * record isn't changed, they're just not counted. A location's band is the number of people
 * whose main location it is. Adding someone who is already counted to a second location changes nothing.
 * Community spaces (churches, meetinghouses, community kitchens) pay a flat price per building. A home pays one price for the
 * house (HOME_PLAN_PRICE_CENTS, below).
 */
import type { Employee, Id, IsoDate, Location, Organization } from "./model.ts";

export type PlanBand = "solo" | "small" | "team" | "kitchen" | "building";

/** Staff-list bands, per location per month (owner decision, 2026-09-27). `max` is inclusive; null = no top. */
export const PLAN_BANDS: readonly { band: Exclude<PlanBand, "building">; label: string; min: number; max: number | null; priceCents: number }[] = [
  { band: "solo", label: "Solo", min: 0, max: 2, priceCents: 800 },
  { band: "small", label: "Small", min: 3, max: 6, priceCents: 2400 },
  { band: "team", label: "Team", min: 7, max: 12, priceCents: 4000 },
  { band: "kitchen", label: "Kitchen", min: 13, max: null, priceCents: 6900 },
];
/** Churches and community kitchens: per building, unlimited volunteers. */
export const BUILDING_PRICE_CENTS = 1900;
/**
 * AI add-on: food photos read by AI (docs/FOOD_STORE.md "Plan: AI add-on", owner decision 2026-09-28): $6 per person
 * per month, counted the same way as the band (once per person per business, at their main location, G1).
 */
export const AI_PHOTOS_PRICE_CENTS = 600;
/**
 * AI RoomCheck add-on (core featuretrials.ts; owner decision 2026-10-04): $7 per person per month, counted exactly like
 * the photos add-on (once per person per business, at their main location, G1).
 */
export const AI_ROOMCHECK_PRICE_CENTS = 700;
/*
 * Owner, 2026-10-07: "Gemini is a paid tier. the $6/person month extra." That is AI_PHOTOS_PRICE_CENTS above: per counted
 * person per month (planFor multiplies it by the counted people), not per location. Unchanged.
 */

/**
 * Home pricing, now (owner, 2026-10-14): "25 days free is way too long for consumer app. We should do 3 days free and then
 * $18/mo". One plan per home (one address, unlimited members): $18 a month, the AI photo features included (the fridge check,
 * Show the fix, food photos), after HOME_TRIAL_DAYS free days that start when the card is added (card required, as for a
 * business). planFor charges it unless `homesBilling: false` (the server's HOMES_BILLING=off, the owner's pause switch).
 *
 * Retired for new sign-ups (owner, 2026-10-09: "$15 for home with AI. $8.99 without."): HOME_PRICE_CENTS and HOME_AI_PRICE_CENTS
 * stay ONLY for a home whose running Stripe subscription already carries one of those two prices (`legacyHomePrice`): it keeps its
 * price and its AI switch ($8.99 off, $15 on) until that subscription ends. A home that starts (or starts again) gets the $18 plan.
 */
export const HOME_PLAN_PRICE_CENTS = 1800;
/** A home's free days (owner, 2026-10-14). Businesses keep TRIAL_DAYS. */
export const HOME_TRIAL_DAYS = 3;
/** Retired home prices (2026-10-09 to 2026-10-14): read for subscriptions already on them, never offered again. */
export const HOME_PRICE_CENTS = 899;
export const HOME_AI_PRICE_CENTS = 1500;
/**
 * The Stripe lookup keys of the two retired home prices, frozen (core payments.ts priceLookupKey returns these for "home" and
 * "home_ai" whatever STRIPE_PRICE_VERSION becomes, so an old subscription is always recognised).
 */
export const LEGACY_HOME_LOOKUP_KEYS = { home: "cc_home_v1", home_ai: "cc_home_ai_v1" } as const;
/** Subscription states in which a stored Stripe item is still being billed (a canceled or expired plan starts over at today's price). */
const LIVE_STATUSES: readonly string[] = ["trialing", "active", "past_due"];

/**
 * A home already subscribed on a retired price: which one its running subscription carries (from the items the signed webhook
 * stored), else null. Null for a business, a home with no plan, and a plan that ended.
 */
export function legacyHomePrice(org: Pick<Organization, "kind"> & { billing?: Organization["billing"] }): "home" | "home_ai" | null {
  const b = org.billing;
  if (org.kind !== "home" || !b || !LIVE_STATUSES.includes(b.status)) return null;
  const items = b.stripe?.items ?? {};
  if (items[LEGACY_HOME_LOOKUP_KEYS.home_ai]) return "home_ai";
  if (items[LEGACY_HOME_LOOKUP_KEYS.home]) return "home";
  return null;
}

/** A home's monthly price: $18 with AI included; a home already on a retired price keeps it ($8.99, or $15 with its AI switch on). */
export const homePriceCents = (org: Pick<Organization, "kind" | "addons"> & { billing?: Organization["billing"] }): number =>
  legacyHomePrice(org) ? (org.addons?.aiPhotos ? HOME_AI_PRICE_CENTS : HOME_PRICE_CENTS) : HOME_PLAN_PRICE_CENTS;

/** "$18" / "$8.99": the shown dollars (the same rule as core payments.ts dollarsOf). */
const usd = (cents: number) => `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`;
/**
 * The home offer in one line, said the same way everywhere (owner, 2026-10-14): "3 days free, then $18/month. Cancel anytime."
 * The site, the app, the emails and the unlock sheet read it from here; nothing types the numbers.
 */
export function homeOfferLine(monthlyCents: number = HOME_PLAN_PRICE_CENTS): string {
  return `${HOME_TRIAL_DAYS} days free, then ${usd(monthlyCents)}/month. Cancel anytime.`;
}
/** The business offer in one line: "25 days free, then $8 to $69 a month per location. Cancel anytime." */
export function businessOfferLine(): string {
  return `${TRIAL_DAYS} days free, then ${usd(PLAN_BANDS[0]!.priceCents)} to ${usd(PLAN_BANDS[PLAN_BANDS.length - 1]!.priceCents)} a month per location. Cancel anytime.`;
}

/** The free days a place gets when its card is added: HOME_TRIAL_DAYS for a home, TRIAL_DAYS for every business. */
export const trialDaysFor = (kind: string | undefined | null): number => (kind === "home" ? HOME_TRIAL_DAYS : TRIAL_DAYS);

export const PLAN_BAND_LABEL: Record<PlanBand, string> = { solo: "Solo", small: "Small", team: "Team", kitchen: "Kitchen", building: "Building" };

export interface LocationPlan {
  locationId: Id;
  /** Counted people (joined or checked in, see countsOnPlan) whose main location this is. */
  people: number;
  band: PlanBand;
  /** The band's price (or the building price). */
  priceCents: number;
  /** Add-ons (photos + RoomCheck) for the people counted here (0 when they're off). */
  addonCents: number;
}

export interface PlanPerson { employeeId: Id; name: string; primaryLocationId: Id | null; locationIds: Id[] }

export interface BusinessPlan {
  locations: LocationPlan[];
  /** Bands plus add-ons. */
  totalCents: number;
  /** The AI add-on: on or off, and what it adds in all. */
  addons: { aiPhotos: boolean; aiPhotosCents: number; aiRoomcheck?: boolean; aiRoomcheckCents?: number };
  /** Everyone on the staff list with where they're counted (null: not counted: inactive, not joined yet, event over, or no location). */
  people: PlanPerson[];
  /** True only when home billing is paused (HOMES_BILLING=off): the home pays nothing. Businesses: always false. */
  free: boolean;
  /**
   * A home's one price; absent for a business. `ai`: the AI photo features are in it (always on the $18 plan). `legacy`: the
   * retired price this home's running subscription is on ("home" $8.99, "home_ai" $15), absent on the $18 plan.
   */
  home?: { priceCents: number; ai: boolean; legacy?: "home" | "home_ai" };
}

/** A location's band from its head count. A location with nobody counted there yet is still Solo (one plan per location). */
export function bandFor(people: number): (typeof PLAN_BANDS)[number] {
  return PLAN_BANDS.find((b) => people >= b.min && (b.max === null || people <= b.max)) ?? PLAN_BANDS[0]!;
}

/** Locations in a stable order: oldest first, then by id. "The business's first location" means the first of these. */
export function orderedLocations(locations: readonly Location[]): Location[] {
  return [...locations].sort((a, b) => (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/**
 * Where a person is counted: their chosen main location when it's still one they work at, else the first of their
 * locations, else the business's first location. `locationIds` empty = all locations. Null when the business has none.
 */
export function mainLocationOf(employee: Pick<Employee, "locationIds" | "primaryLocationId">, locations: readonly Location[]): Id | null {
  const known = new Set(locations.map((l) => l.id));
  const works = employee.locationIds.filter((id) => known.has(id));
  const p = employee.primaryLocationId;
  if (p && known.has(p) && (employee.locationIds.length === 0 || works.includes(p))) return p;
  if (works.length) return works[0]!;
  return orderedLocations(locations)[0]?.id ?? null;
}

export interface PlanOptions {
  /** The business's local date today (its state's zone): a volunteer whose `endsOn` is before it isn't counted. Unset: nobody's event is over. */
  today?: IsoDate;
  /** Employees with at least one check-in (a shop-tablet person counts from their first check-in). */
  checkedIn?: ReadonlySet<Id> | readonly Id[];
  /** Homes pay (owner, 2026-10-09). Default true; false = the owner paused home billing (HOMES_BILLING=off): homes are free. */
  homesBilling?: boolean;
}

/** Does this person count on the plan today? Active, joined (or checked in), and not past their event or their last day. */
export function countsOnPlan(e: Pick<Employee, "id" | "active" | "userId" | "endsOn"> & Partial<Pick<Employee, "lastDay">>, o: PlanOptions = {}): boolean {
  if (!e.active) return false;
  const checked = o.checkedIn ? (o.checkedIn instanceof Set ? o.checkedIn.has(e.id) : (o.checkedIn as readonly Id[]).includes(e.id)) : false;
  if (!e.userId && !checked) return false;
  // A last day (People → Set last day) works like a volunteer's endsOn: counted through it, not the day after.
  if (e.lastDay && o.today && o.today > e.lastDay) return false;
  return !(e.endsOn && o.today && o.today > e.endsOn);
}

/** The plan for one business (or home). Only this org's locations and employees are counted. */
export function planFor(org: Pick<Organization, "id" | "kind" | "addons"> & { billing?: Organization["billing"] }, locations: readonly Location[], employees: readonly Employee[], opts: PlanOptions = {}): BusinessPlan {
  const locs = orderedLocations(locations.filter((l) => l.orgId === org.id));
  const staff = employees.filter((e) => e.orgId === org.id);
  const counted = new Map<Id, number>(locs.map((l) => [l.id, 0]));
  const people: PlanPerson[] = staff.map((e) => {
    const main = countsOnPlan(e, opts) ? mainLocationOf(e, locs) : null;
    if (main) counted.set(main, (counted.get(main) ?? 0) + 1);
    return { employeeId: e.id, name: e.name, primaryLocationId: main, locationIds: e.locationIds };
  });
  if (org.kind === "home") {
    // One price per home (not per person, not per location); the AI is in the price, never a per-person add-on here.
    const paid = opts.homesBilling !== false;
    const priceCents = paid ? homePriceCents(org) : 0;
    const legacy = legacyHomePrice(org);
    const home = legacy ? { priceCents, ai: !!org.addons?.aiPhotos, legacy: org.addons?.aiPhotos ? "home_ai" as const : "home" as const } : { priceCents, ai: true };
    return { locations: locs.map((l) => ({ locationId: l.id, people: counted.get(l.id) ?? 0, band: "solo", priceCents: 0, addonCents: 0 })), totalCents: priceCents, addons: { aiPhotos: !!org.addons?.aiPhotos, aiPhotosCents: 0, ...(org.addons?.aiRoomcheck ? { aiRoomcheck: true, aiRoomcheckCents: 0 } : {}) }, people, free: !paid, ...(paid ? { home } : {}) };
  }
  const ai = !!org.addons?.aiPhotos;
  const rc = !!org.addons?.aiRoomcheck;
  const plans: LocationPlan[] = locs.map((l) => {
    const n = counted.get(l.id) ?? 0;
    const addonCents = (ai ? n * AI_PHOTOS_PRICE_CENTS : 0) + (rc ? n * AI_ROOMCHECK_PRICE_CENTS : 0);
    if (org.kind === "community_space") return { locationId: l.id, people: n, band: "building", priceCents: BUILDING_PRICE_CENTS, addonCents };
    const b = bandFor(n);
    return { locationId: l.id, people: n, band: b.band, priceCents: b.priceCents, addonCents };
  });
  const counted_ = plans.reduce((s, p) => s + p.people, 0);
  const aiPhotosCents = ai ? counted_ * AI_PHOTOS_PRICE_CENTS : 0;
  const aiRoomcheckCents = rc ? counted_ * AI_ROOMCHECK_PRICE_CENTS : 0;
  return { locations: plans, totalCents: plans.reduce((s, p) => s + p.priceCents, 0) + aiPhotosCents + aiRoomcheckCents, addons: { aiPhotos: ai, aiPhotosCents, ...(rc ? { aiRoomcheck: true, aiRoomcheckCents } : {}) }, people, free: false };
}

const ORDINAL = (n: number) => { const t = n % 100, u = n % 10; return `${n}${t >= 11 && t <= 13 ? "th" : u === 1 ? "st" : u === 2 ? "nd" : u === 3 ? "rd" : "th"}`; };
const dollarsOf = (cents: number) => `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`;

/**
 * The live price line in "Add someone" (Wave 1 #8): what the plan will be once this person joins, at the location
 * they'd count at. "Adding Sam keeps you at Small, $24." or "This is your 7th person: Team, $40 a month."
 * Null for homes (free) or a business with no location.
 */
export function addPersonLine(plan: { locations: readonly Pick<LocationPlan, "locationId" | "people" | "band" | "priceCents">[]; free?: boolean; home?: unknown }, name: string, locationIds: readonly Id[] = []): string | null {
  // A home's price never changes with its people (one price per home), so there is no line.
  if (plan.free || plan.home || !plan.locations.length) return null;
  const at = plan.locations.find((l) => locationIds.includes(l.locationId)) ?? plan.locations[0]!;
  const first = name.trim().split(/\s+/)[0] || "someone";
  if (at.band === "building") return `Adding ${first} keeps you at ${PLAN_BAND_LABEL.building}, ${dollarsOf(at.priceCents)}.`;
  const next = bandFor(at.people + 1);
  if (next.band === at.band) return `Adding ${first} keeps you at ${next.label}, ${dollarsOf(next.priceCents)}.`;
  return `This is your ${ORDINAL(at.people + 1)} person: ${next.label}, ${dollarsOf(next.priceCents)} a month.`;
}

/** Said under the plan: invites are free until the person joins. */
export const PLAN_COUNT_NOTE = "Invited people count once they join.";

/**
 * Free trial: 25 days (owner decision 2026-09-28, "to really get ingrained"; 2026-09-29, "Trial starts when the card is
 * added"). The 25 days are Stripe's: Checkout opens the subscription with `trial_period_days: 25` when the card is added
 * (docs/PAYMENTS.md), and the countdown shown is read only from the `trial_end` Stripe's signed webhook stored (core
 * payments.ts `trialFromBilling`). Before a card there is no trial clock at all, however old the business is: every
 * feature works and only the calm card prompt and banner appear (B = MAT). A home's trial is HOME_TRIAL_DAYS (trialDaysFor).
 *
 * Negation (rejected, the rule until 2026-09-29): 25 days from the day the business was created. An owner who used the
 * app free for three weeks and then added a card would see "4 days left" instead of the 25 the site promised.
 */
export const TRIAL_DAYS = 25;

export interface TrialStatus {
  inTrial: boolean;
  /** The last free day: the day of Stripe's trial_end in the business's own state's zone (payments.ts), e.g. "2026-10-23". */
  endsOn: string;
  /** Whole days left (0 once it has ended). */
  daysLeft: number;
  /** The trial's length for this place (trialDaysFor: TRIAL_DAYS, or HOME_TRIAL_DAYS for a home); sent so screens don't hard-code it. */
  days: number;
}

/** "Free trial: 12 days left (ends Oct 23)" / "Free trial: last day today (ends Oct 23)" / "Free trial ended Oct 23". */
export function trialLine(t: TrialStatus): string {
  const pretty = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short", day: "numeric" }).format(new Date(`${t.endsOn}T12:00:00Z`));
  if (!t.inTrial) return `Free trial ended ${pretty}`;
  if (t.daysLeft === 1) return `Free trial: last day today (ends ${pretty})`;
  return `Free trial: ${t.daysLeft} days left (ends ${pretty})`;
}
